import "./DestinationSights.css";

import { Controller } from "@hotwired/stimulus";
import { HorizontalRailScroller } from "../../Shared/HorizontalRail/HorizontalRailDrag";
import { localizeRegionDisplayNames } from "../../Shared/RegionDisplayNames";
import { uiLanguage } from "../../Shared/webTranslations";

type TurboVisitOptions = {
    action?: "advance" | "replace" | "restore"
};

type TurboWindow = Window & {
    Turbo?: {
        visit: (location: string, options?: TurboVisitOptions) => void
    }
};

const SearchDebounceMilliseconds = 350;
const ListFrameId = "destination-sights-list";
const FilterSelector = ".destination-sights-filter[data-filter-id]";
const SelectedFilterClass = "destination-sights-filter--selected";
const DefaultFilterId = "seeAndDo";
const NoFilterRequestHeader = "X-SmartGuide-Destination-Sights-Filter";
const NoDefaultFilterQueryParameter = "noDefaultFilter";

export default class DestinationSightsController extends Controller<HTMLElement> {
    static targets = ["search"];

    declare readonly searchTarget: HTMLInputElement;
    declare readonly hasSearchTarget: boolean;

    private searchTimer: number | undefined;
    private horizontalRailScroller?: HorizontalRailScroller;
    private refreshSequence = 0;
    private regionDisplayNamesObserver?: MutationObserver;

    connect(): void {
        this.horizontalRailScroller = new HorizontalRailScroller({
            root: this.element,
            railSelector: ".destination-sights-filters",
            scrollLeftDataAttribute: "destinationSightsFiltersScrollLeft"
        });
        this.horizontalRailScroller.connect();
        localizeRegionDisplayNames(this.element, uiLanguage);
        this.observeRegionDisplayNames();
    }

    disconnect(): void {
        if (this.searchTimer !== undefined) {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = undefined;
        }

        this.horizontalRailScroller?.disconnect();
        this.horizontalRailScroller = undefined;
        this.regionDisplayNamesObserver?.disconnect();
        this.regionDisplayNamesObserver = undefined;
    }

    search(): void {
        if (!this.hasSearchTarget) {
            return;
        }

        if (this.searchTimer !== undefined) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.searchTimer = undefined;
            void this.refreshResultsWithSearch(this.searchTarget.value);
        }, SearchDebounceMilliseconds);
    }

    selectFilter(event: Event): void {
        const link = event.currentTarget as HTMLAnchorElement | null;
        if (!link) {
            return;
        }

        const href = link.getAttribute("href");
        if (!href) {
            return;
        }

        event.preventDefault();
        const suppressDefaultFilter = this.isSelectedFilterToggleOffWithoutSearch(link);
        const url = this.urlWithCurrentSearch(href);
        this.updateFilterSelection(url, suppressDefaultFilter);
        void this.refreshResults(url, suppressDefaultFilter);
    }

    private async refreshResultsWithSearch(rawValue: string): Promise<void> {
        const url = new URL(this.urlFromVisibleState(), window.location.href);
        const query = rawValue.trim();
        if (query.length > 0) {
            url.searchParams.set("q", query);
        } else {
            url.searchParams.delete("q");
        }

        url.searchParams.delete("offset");
        url.searchParams.delete(NoDefaultFilterQueryParameter);
        const nextUrl = `${url.pathname}${url.search}`;
        const suppressDefaultFilter = query.length === 0 &&
            !url.searchParams.has("filter") &&
            !this.hasSelectedFilter();

        this.updateFilterSelection(nextUrl, suppressDefaultFilter);
        await this.refreshResults(nextUrl, suppressDefaultFilter);
    }

    private urlWithCurrentSearch(href: string): string {
        const url = new URL(href, window.location.href);
        const query = this.hasSearchTarget ? this.searchTarget.value.trim() : url.searchParams.get("q")?.trim() ?? "";

        if (query.length > 0) {
            url.searchParams.set("q", query);
        } else {
            url.searchParams.delete("q");
        }

        url.searchParams.delete("offset");
        url.searchParams.delete(NoDefaultFilterQueryParameter);
        return `${url.pathname}${url.search}`;
    }

    private async refreshResults(url: string, suppressDefaultFilter = false): Promise<void> {
        const sequence = ++this.refreshSequence;
        const headers: Record<string, string> = { Accept: "text/html" };
        if (suppressDefaultFilter) {
            headers[NoFilterRequestHeader] = "none";
        }

        try {
            const response = await fetch(url, {
                credentials: "same-origin",
                headers
            });
            if (!response.ok) {
                this.visit(url);
                return;
            }

            const html = await response.text();
            const documentFragment = new DOMParser().parseFromString(html, "text/html");
            const nextFrame = documentFragment.getElementById(ListFrameId);
            const currentFrame = document.getElementById(ListFrameId);
            if (!nextFrame || !currentFrame) {
                this.visit(url);
                return;
            }

            if (sequence !== this.refreshSequence) {
                return;
            }

            const importedFrame = document.importNode(nextFrame, true);
            currentFrame.replaceWith(importedFrame);
            localizeRegionDisplayNames(importedFrame, uiLanguage);
        } catch {
            if (sequence !== this.refreshSequence) {
                return;
            }

            this.visit(url);
        }
    }

    private updateFilterSelection(url: string, suppressDefaultFilter = false): void {
        const selectedUrl = new URL(url, window.location.href);
        const searchTerm = selectedUrl.searchParams.get("q")?.trim() ?? "";
        const selectedFilterId = selectedUrl.searchParams.get("filter")?.trim() ||
            (!suppressDefaultFilter && searchTerm.length === 0 ? DefaultFilterId : "");

        this.element.querySelectorAll<HTMLAnchorElement>(FilterSelector).forEach(link => {
            const filterId = link.dataset.filterId?.trim() ?? "";
            const isSelected = selectedFilterId.length > 0 && filterId === selectedFilterId;
            link.classList.toggle(SelectedFilterClass, isSelected);
            link.setAttribute("aria-pressed", isSelected ? "true" : "false");
            link.setAttribute("href", this.filterHref(selectedUrl.pathname, isSelected ? "" : filterId, searchTerm));
        });
    }

    private observeRegionDisplayNames(): void {
        this.regionDisplayNamesObserver?.disconnect();
        this.regionDisplayNamesObserver = new MutationObserver(records => {
            records.forEach(record => {
                record.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        localizeRegionDisplayNames(node, uiLanguage);
                    }
                });
            });
        });
        this.regionDisplayNamesObserver.observe(this.element, { childList: true, subtree: true });
    }

    private hasSelectedFilter(): boolean {
        return Array.from(this.element.querySelectorAll<HTMLElement>(FilterSelector))
            .some(link => link.classList.contains(SelectedFilterClass));
    }

    private filterHref(pathname: string, filterId: string, searchTerm: string): string {
        const searchParams = new URLSearchParams();
        if (filterId.length > 0 && (filterId !== DefaultFilterId || searchTerm.length > 0)) {
            searchParams.set("filter", filterId);
        }

        if (searchTerm.length > 0) {
            searchParams.set("q", searchTerm);
        }

        const query = searchParams.toString();
        return query.length > 0 ? `${pathname}?${query}` : pathname;
    }

    private urlFromVisibleState(): string {
        const url = new URL(window.location.href);
        const filters = Array.from(this.element.querySelectorAll<HTMLElement>(FilterSelector));
        const selectedFilterId = filters
            .find(filter => filter.classList.contains(SelectedFilterClass))
            ?.dataset.filterId
            ?.trim() ?? "";
        const searchTerm = this.hasSearchTarget ? this.searchTarget.value.trim() : url.searchParams.get("q")?.trim() ?? "";

        if (filters.length > 0) {
            if (selectedFilterId.length > 0) {
                url.searchParams.set("filter", selectedFilterId);
            } else {
                url.searchParams.delete("filter");
            }
        }

        if (searchTerm.length > 0) {
            url.searchParams.set("q", searchTerm);
        } else {
            url.searchParams.delete("q");
        }

        url.searchParams.delete("offset");
        url.searchParams.delete(NoDefaultFilterQueryParameter);
        return `${url.pathname}${url.search}`;
    }

    private isSelectedFilterToggleOffWithoutSearch(link: HTMLAnchorElement): boolean {
        const searchTerm = this.hasSearchTarget ? this.searchTarget.value.trim() : "";
        return searchTerm.length === 0 &&
            link.classList.contains(SelectedFilterClass);
    }

    private visit(url: string): void {
        const turbo = (window as TurboWindow).Turbo;
        if (turbo) {
            turbo.visit(url, { action: "replace" });
            return;
        }

        window.location.assign(url);
    }
}
